class DesignersList {
  constructor() {
    this.container = document.querySelector('.designers-sections');
    this.searchInput = document.querySelector('.designers-search__input');
    this.alphaNav = document.querySelector('.designers-alpha-nav__list');
    this.noResults = document.querySelector('.designers-no-results');
    this.loading = document.querySelector('.designers-loading');

    if (!this.container) return;

    this.alphabet = ['0-9','A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'];
    this.collections = [];
    const designersPage = document.querySelector('.designers-page');
    const override = designersPage?.getAttribute('data-department-override');
    this.isDepartmentLocked = !!override;
    this.currentDepartment = override || window.activeDepartment || 'women';
    this.CACHE_KEY = 'italist_designers_collections';
    this.CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours

    this.init();
  }

  async init() {
    await this.loadCollections();
    this.render();
    this.bindEvents();
    if (!this.isDepartmentLocked) this.observeDepartmentChanges();
  }

  async loadCollections() {
    const cached = this.getCache();
    if (cached) {
      this.collections = cached;
      return;
    }
    await this.fetchAllCollections();
    this.setCache(this.collections);
  }

  getCache() {
    try {
      const raw = localStorage.getItem(this.CACHE_KEY);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp > this.CACHE_TTL) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  setCache(data) {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify({
        data: data.map(c => ({ handle: c.handle, title: c.title, products_count: c.products_count })),
        timestamp: Date.now()
      }));
    } catch (e) {
      // localStorage full or unavailable
    }
  }

  async fetchAllCollections() {
    const root = window.Shopify.routes.root;
    try {
      const firstRes = await fetch(`${root}collections.json?limit=250&page=1`);
      const firstData = await firstRes.json();
      this.collections = firstData.collections || [];

      if (this.collections.length === 250) {
        const pagePromises = [];
        for (let page = 2; page <= 10; page++) {
          pagePromises.push(
            fetch(`${root}collections.json?limit=250&page=${page}`)
              .then(r => r.json())
              .then(d => d.collections || [])
              .catch(() => [])
          );
        }
        const results = await Promise.all(pagePromises);
        results.forEach(cols => {
          if (cols.length > 0) this.collections = this.collections.concat(cols);
        });
      }
    } catch (e) {
      console.error('Designers: Failed to fetch collections', e);
    }
  }

  getBrandsForDepartment(department) {
    const suffix = `-${department}`;

    const EXCLUDED_PREFIXES = new Set([
      'backpacks', 'belts', 'boots', 'bracelets', 'brooches', 'clutches', 'coats-jackets', 'cufflinks', 'curated-for-you', 'dresses', 'flat-shoes', 'fleeces-tracksuits', 'earrings', 'eyeglasses', 'eyewear', 'gifts-under-500', 'gloves', 'hats', 'hair-accessories', 'hi-tech-accessories', 'high-heeled-shoes', 'jeans', 'jumpsuits', 'keyrings', 'laced-shoes', 'luggage', 'necklaces', 'new-arrivals', 'pendants', 'rings', 'scarves-wraps', 'suits', 'sweaters', 'swimwear', 'sandals', 'scarves', 'shoulder-bags', 'skirts', 'sneakers', 'sunglasses', 'ties', 'topwear', 'trending', 'totes', 'umbrellas', 'underwear-nightwear', 'wallets', 'watches', 'wedges', 'yogawear'
    ]);

    const brandMap = new Map();

    this.collections.forEach(col => {
      if (!col.handle.endsWith(suffix)) return;
      if (col.products_count === 0) return;

      // Derive the prefix by stripping the -{department} suffix
      const prefix = col.handle.slice(0, col.handle.length - suffix.length);

      // Skip if the prefix matches any known category slug
      if (EXCLUDED_PREFIXES.has(prefix)) return;

      const brandName = col.title;
      if (!brandMap.has(brandName)) {
        brandMap.set(brandName, {
          name: brandName,
          url: `${window.Shopify.routes.root}collections/${col.handle}`,
          handle: col.handle
        });
      }
    });

    const brands = Array.from(brandMap.values());
    brands.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return brands;
  }

  groupByLetter(brands) {
    const groups = {};
    this.alphabet.forEach(letter => { groups[letter] = []; });

    brands.forEach(brand => {
      const firstChar = brand.name.charAt(0).toUpperCase();
      if (/\d/.test(firstChar)) {
        groups['0-9'].push(brand);
      } else if (groups[firstChar]) {
        groups[firstChar].push(brand);
      }
    });

    return groups;
  }

  render() {
    const brands = this.getBrandsForDepartment(this.currentDepartment);
    const groups = this.groupByLetter(brands);

    if (this.loading) this.loading.style.display = 'none';

    let html = '';
    this.alphabet.forEach(letter => {
      if (groups[letter].length === 0) return;
      html += `<div class="designers-section" id="designers-${letter}">`;
      html += `<h2 class="designers-section__letter">${letter}</h2>`;
      html += '<div class="designers-section__grid">';
      groups[letter].forEach(brand => {
        html += `<a href="${brand.url}" class="designers-section__link">${brand.name}</a>`;
      });
      html += '</div></div>';
    });
    this.container.innerHTML = html;

    this.updateAlphaNav();

    if (this.searchInput) this.searchInput.value = '';
    if (this.noResults) this.noResults.style.display = 'none';
  }

  updateAlphaNav() {
    if (!this.alphaNav) return;

    let html = '';
    this.alphabet.forEach(letter => {
      const section = document.getElementById('designers-' + letter);
      const disabled = !section ? ' designers-alpha-nav__link--disabled' : '';
      html += `<li><a href="#designers-${letter}" class="designers-alpha-nav__link${disabled}" data-letter="${letter}">${letter}</a></li>`;
    });
    this.alphaNav.innerHTML = html;
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.handleSearch());
    }

    document.addEventListener('click', (e) => {
      const alphaLink = e.target.closest('.designers-alpha-nav__link');
      if (alphaLink && !alphaLink.classList.contains('designers-alpha-nav__link--disabled')) {
        e.preventDefault();
        const letter = alphaLink.dataset.letter;
        const section = document.getElementById('designers-' + letter);
        if (section && !section.classList.contains('designers-section--hidden')) {
          const navHeight = document.querySelector('.designers-alpha-nav').offsetHeight;
          const top = section.getBoundingClientRect().top + window.scrollY - navHeight - 16;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    });
  }

  handleSearch() {
    const query = this.searchInput.value.toLowerCase().trim();
    const links = this.container.querySelectorAll('.designers-section__link');
    const sections = this.container.querySelectorAll('.designers-section');
    let visibleCount = 0;

    links.forEach(link => {
      const match = !query || link.textContent.toLowerCase().includes(query);
      link.classList.toggle('designers-section__link--hidden', !match);
      if (match) visibleCount++;
    });

    sections.forEach(section => {
      const visible = section.querySelectorAll('.designers-section__link:not(.designers-section__link--hidden)');
      section.classList.toggle('designers-section--hidden', visible.length === 0);
    });

    this.updateAlphaNav();
    if (this.noResults) {
      this.noResults.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  observeDepartmentChanges() {
    document.addEventListener('departmentChange', (e) => {
      const dept = e.detail.department;
      if (dept && dept !== 'kids' && dept !== this.currentDepartment) {
        this.currentDepartment = dept;
        this.render();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DesignersList());
} else {
  new DesignersList();
}
